import type { BulkAllocateItem } from '@/services/cart';

export interface ReserveStockItemInput {
  id: string;
  itemName: string;
  catalogId?: string | null;
  ieKitId?: string | null;
  need: number;
  reservedQty?: number;
  existingPlaceholderQty?: number;
  requiresSize: boolean;
  selectedCatalogId?: string | null;
  maxAllocatableQty?: number | null;
  placeholderCatalogId?: string | null;
  allSizesUnavailable?: boolean;
}

export interface ReserveStockPlaceholderPlan {
  parentCatalogId: string;
  quantity: number;
  added: number;
  itemName: string;
}

export interface ReserveStockPlan {
  allocations: BulkAllocateItem[];
  placeholders: ReserveStockPlaceholderPlan[];
  missingDueToStock: number;
}

export const buildReserveStockPlan = (
  items: ReserveStockItemInput[],
  locationResolver?: (catalogId: string, itemId: string) => string | undefined,
): ReserveStockPlan => {
  const allocations: BulkAllocateItem[] = [];
  const placeholderGroups = new Map<
    string,
    { required: number; existing: number; itemName: string }
  >();
  let missingDueToStock = 0;

  items.forEach((item) => {
    const reservedQty = item.reservedQty ?? 0;
    const existingPlaceholderQty = item.existingPlaceholderQty ?? 0;
    const effectiveNeed = Math.max(item.need - reservedQty, 0);

    if (effectiveNeed <= 0) {
      return;
    }

    if (item.requiresSize && !item.selectedCatalogId) {
      if (item.allSizesUnavailable) {
        missingDueToStock += effectiveNeed;
        return;
      }

      const parentCatalogId = item.placeholderCatalogId ?? item.catalogId ?? null;
      if (!parentCatalogId) {
        return;
      }

      const entry = placeholderGroups.get(parentCatalogId) ?? {
        required: 0,
        existing: existingPlaceholderQty,
        itemName: item.itemName,
      };

      entry.required += effectiveNeed;
      entry.existing = Math.max(entry.existing, existingPlaceholderQty);
      placeholderGroups.set(parentCatalogId, entry);
      return;
    }

    const availableQty =
      typeof item.maxAllocatableQty === 'number' ? Math.max(item.maxAllocatableQty, 0) : Infinity;
    const shortfall = Math.max(effectiveNeed - availableQty, 0);
    if (shortfall > 0) {
      missingDueToStock += shortfall;
    }

    const catalogId = item.requiresSize ? item.selectedCatalogId : item.catalogId;

    if (!catalogId) {
      return;
    }

    let allocatable = effectiveNeed;
    allocatable = Math.min(allocatable, availableQty);

    if (allocatable <= 0) {
      return;
    }

    allocations.push({
      quantity: allocatable,
      item_name: item.itemName,
      catalog_id: catalogId,
      ie_kit_id: item.ieKitId ?? null,
      ...(locationResolver
        ? { location_id: locationResolver(catalogId, item.id) ?? null }
        : {}),
    });
  });

  const placeholders: ReserveStockPlaceholderPlan[] = [];
  placeholderGroups.forEach((entry, parentCatalogId) => {
    if (entry.required <= 0) {
      return;
    }

    placeholders.push({
      parentCatalogId,
      quantity: entry.required,
      added: Math.max(entry.required - entry.existing, 0),
      itemName: entry.itemName,
    });
  });

  return { allocations, placeholders, missingDueToStock };
};
